import React from 'react';
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';
import { Event, EventType, EventResponseSummary, getEventTypeLabel, eventGroupLabel } from '../../types/event';
import { formatVenueSections } from '../../utils/venueSections';

interface EventCardProps {
  event: Event;
  responseSummary?: EventResponseSummary;
  onClick: () => void;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  responseSummary,
  onClick,
}) => {
  const getEventTypeColor = (type: EventType) => {
    switch (type) {
      case EventType.Training:
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case EventType.Match:
        return 'bg-red-100 text-red-800 border-red-200';
      case EventType.Meeting:
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case EventType.Other:
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${start.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const isPast = new Date(event.endTime) < new Date();

  return (
    <div
      className={`bg-white rounded-lg shadow-md border hover:shadow-lg transition-shadow ${
        isPast ? 'opacity-60' : ''
      }`}
    >
      <div className="p-3 md:p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 cursor-pointer" onClick={onClick}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full border ${getEventTypeColor(
                  event.type
                )}`}
              >
                {getEventTypeLabel(event.type)}
              </span>
              {eventGroupLabel(event) && (
                <span className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                  {eventGroupLabel(event)}
                </span>
              )}
              {isPast && (
                <span className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-full">
                  Lezárult
                </span>
              )}
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900">{event.title}</h3>
            {event.description && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                {event.description}
              </p>
            )}
          </div>
        </div>

        {/* Event details */}
        <div className="space-y-2 mb-4 cursor-pointer" onClick={onClick}>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <CalendarIcon className="h-4 w-4" />
            <span>{formatDate(event.startTime)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <ClockIcon className="h-4 w-4" />
            <span>{formatTime(event.startTime, event.endTime)}</span>
          </div>
          {(event.location || event.venue) && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPinIcon className="h-4 w-4" />
              <span>{event.venue?.name || event.location}{formatVenueSections(event.venueSections, event.venue?.sectionNames) ? ` (${formatVenueSections(event.venueSections, event.venue?.sectionNames)})` : ''}</span>
            </div>
          )}
        </div>

        {/* Response summary */}
        {responseSummary && event.responsesEnabled && (
          <div className="flex items-center gap-2 md:gap-4 mb-4 py-2 px-2 md:px-3 bg-gray-50 rounded-lg">
            <UserGroupIcon className="h-5 w-5 text-gray-500 shrink-0" />
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
              <span className="text-green-600 font-medium">
                {responseSummary.attendingCount} jön
              </span>
              <span className="text-red-600 font-medium">
                {responseSummary.notAttendingCount} nem jön
              </span>
              {responseSummary.noResponseCount > 0 && (
                <span className="text-gray-500">
                  {responseSummary.noResponseCount} nem válaszolt
                </span>
              )}
            </div>
          </div>
        )}

        {/* Response buttons removed - responses are only available on Dashboard */}

        {/* Comment count */}
        <div
          className="flex items-center gap-1 mt-3 text-sm text-gray-500 cursor-pointer hover:text-primary-600"
          onClick={onClick}
        >
          <ChatBubbleLeftIcon className="h-4 w-4 shrink-0" />
          <span>{event.commentCount ?? 0} hozzászólás</span>
        </div>
      </div>
    </div>
  );
};

export default EventCard;
